import mapValues from 'lodash/mapValues';

import { alignLeftCellStyle } from '@client-shared/ui-agGrid';

import {
  enabledFlagColumnDefinition,
  companyCodeColumnDefinition,
  legalEntityColumnDefinition,
  countryTaxTypeColumnDefinition,
  chartNameColumnDefinition,
  usTaxTypeColumnDefinition,
  localTaxTypeColumnDefinition,
  functionalCurrencyColumnDefinition,
  taxCurrencyColumnDefinition,
  recordFTCColumnDefinition,
  federalFTCLimitColumnDefinition,
  stateMinimumTaxColumDefinition,
  stateApportionmentRateColumnDefinition,
  statePrePostNOLColumnDefinition,
  NOLColumnDefinition,
  recordVAColumnDefinition,
  currentTaxRateColumnDefinition,
  deferredTaxRateColumnDefinition,
  filingGroupColumnDefinition,
  secondaryTaxColumnDefinition,
  countryCodeColumnDefinition,
  countryTaxTypeDescriptionColumnDefinition,
  commentColumnDefinition,
  adminColumnSharedFields,
  statCurrencyColumnDefinition,
  homeOfficeCompanyCodeColumnDefinition,
  diagnosticsSeverityColumnDefinition,
  diagnosticWithTooltipColumnDefinition,
} from '../../shared/columnDefinitions';
import appendIndicatorRenderer from '../../shared/gridEdit/validationMessagesIndicatorCellRenderer';
import { percentageFormatter } from '../../shared/withOverride/formatters';

import ObligationsAdminColumnRenderer from './obligationsAdminColumnRenderer.component';
import { GridMode } from '../../shared/gridEdit/useEditableGridLogic';
import NumericCellEditor from '../../shared/agGrid/cellEditors/NumericCellEditor';
import SelectCellEditor, { baseCellEditorParams } from '../../shared/gridEdit/selectCellEditor.component';
import { allowOnlyParser, floatParser, nullableFloatParser } from '../../shared/gridEdit/utils/parsers';
import { getQuickFilterTextWithSkippingAddedRow } from '../../shared/gridEdit/quickFilterUtils';
import { NO_FLAG, YES_FLAG } from '../../shared/constants';
import {
  SecondaryTax,
  countryCodes,
  NOLValues,
  usCountryTaxTypes,
  obligationBasedFieldsToCopy,
  companyCodeBasedFieldsToCopy,
  fieldsToCopyWhenDomicileCountryCode,
} from './constants';
import { getObligationUniqueId } from './helpers';
import { decodeCountryTaxType, encodeCountryTaxType } from '../../utils/encodeDecodeCTT';

const addEditableProperties = (
  columnDefinition,
  editableWhen,
  setRowData,
  {
    additionalCondition,
    valueParser,
    valueSetterFactory,
    valueGetter,
    cellRenderer,
  } = {},
  lookUpData
) => {
  const editable = ({ data, context }) => {
    if (!context || !data) {
      return false;
    }
    return editableWhen.some(allowed => allowed === context.mode)
      && (
        (context.mode === GridMode.Edit && context.editedRowId === getObligationUniqueId(data))
        || (context.mode === GridMode.Add && getObligationUniqueId(data) === null)
      )
      && (additionalCondition || (() => true))({ data, lookUpData, context });
  };
  return ({
    ...columnDefinition,
    editable,
    cellClassRules: {
      'disable-editing': args => !editable(args),
    },
    valueSetter: valueSetterFactory
      ? valueSetterFactory({ lookUpData, setRowData })
      : params => setRowData(params),
    filterParams: { newRowsAction: 'keep' },
    valueParser,
    valueGetter,
    ...(cellRenderer ? { cellRenderer } : {}),
  });
};

const addSourceIndicatorProperties = (columnDefinition, options = {}) => ({
  ...columnDefinition,
  customExport: {
    ...columnDefinition.customExport,
    overrideConfig: {
      sourceField: `${columnDefinition.field}Source`,
      valueField: columnDefinition.field,
      ...options,
    },
  },
});

const addDefaultProperties = columnDefinition => ({
  ...columnDefinition,
  cellClassRules: {
    'disable-editing': () => true,
  },
  filterParams: { newRowsAction: 'keep' },
});

const addWithDropdownProperties = (columnDefinition, config = {}, context = {}) => ({
  ...columnDefinition,
  cellEditor: SelectCellEditor,
  cellEditorPopup: true,
  cellEditorParams: rowParams => ({
    options: config.optionsGetter ? config.optionsGetter(context, rowParams) : [],
    ...baseCellEditorParams,
    ...config.cellEditorParams ? config.cellEditorParams : {},
  }),
});

const addPopupProperties = cellEditor => ({
  cellEditorPopup: true,
  cellEditor,
});

export const areUSFederalFieldsEditable = ({ data }) => data.countryCode === countryCodes.US
  && data.countryTaxType === usCountryTaxTypes.US2;

export const areStateFieldsEditable = ({ data }) => data.countryCode === countryCodes.US
  && data.countryTaxType !== usCountryTaxTypes.US2;

const hasDomicileCountryCode = ({ data, lookUpData }) => {
  const foundOption = lookUpData.companyCodeOptions.find(option => option.value === data.companyCode);
  return foundOption
    && foundOption.associatedLeCountryCode === data.countryCode
    && data.countryCode !== countryCodes.US;
};

const isRecordVAEditable = ({ data }) => data.NOL === NOLValues.RECORD;

export const isNOLFieldEditable = ({ data, lookUpData }) => {
  const foundOption = lookUpData.companyCodeOptions.find(option => option.value === data.companyCode);
  return foundOption && foundOption.subledgerFlag === YES_FLAG;
};

export const isTaxCurrencyFieldEditable = ({ data, lookUpData, context }) => {
  if (data.countryCode === countryCodes.US) {
    return false;
  }

  const foundOption = lookUpData.companyCodeOptions.find(option => option.value === data.companyCode);

  return (foundOption && foundOption.subledgerFlag === NO_FLAG)
    || context.canEditTaxCurrencySubledger;
};

export const isStatePrePostNOLFieldEditable = ({ data }) => data.countryCode === countryCodes.US
  && data.countryTaxType !== usCountryTaxTypes.US2
  && data.NOL === NOLValues.UTILIZE;

const isSecondaryTaxBranch = ({ data }) => data.secondaryTax === SecondaryTax.Branch;

const getDataFromCountryTaxType = (encodedCountryTaxType, taxCurrencyDefaultCodes, countryTaxTypesOptions) => {
  const countryTaxTypeData = countryTaxTypesOptions.find(({ value }) => value === encodedCountryTaxType);

  const { countryCode, countryTaxType } = countryTaxTypeData
    ? decodeCountryTaxType(countryTaxTypeData.value)
    : { countryCode: null, countryTaxType: null };

  const taxCurrency = taxCurrencyDefaultCodes[countryCode]
    ? taxCurrencyDefaultCodes[countryCode].currencyCode
    : null;


  return ({
    countryCode,
    countryTaxType,
    taxCurrency,
    taxCurrencySource: taxCurrency,
    countryTaxTypeDescription: countryTaxTypeData ? countryTaxTypeData.description : null,
    deferredTaxRate: countryTaxTypeData ? countryTaxTypeData.defTaxRate : null,
    currentTaxRate: countryTaxTypeData ? countryTaxTypeData.taxRate : null,
  });
};

const copyWhenDomicileCountryCode = ({ data, lookUpData }) => mapValues(
  fieldsToCopyWhenDomicileCountryCode,
  hasDomicileCountryCode({ data, lookUpData })
    ? value => lookUpData.companyCodeBasedInitialData[data.companyCode][value]
    : () => null
);

const columnDefinitionConfig = ([
  {
    baseColumnDefinition: enabledFlagColumnDefinition,
    editableWhen: [GridMode.Edit, GridMode.Add],
    editableWhenConfig: {
      valueParser: allowOnlyParser([YES_FLAG, NO_FLAG]),
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.EnabledFlagOptions,
      cellEditorParams: {
        minWidth: 200,
      },
    },
  },
  {
    baseColumnDefinition: companyCodeColumnDefinition,
    editableWhen: [GridMode.Add],
    editableWhenConfig: {
      valueSetterFactory: ({ lookUpData, setRowData }) => params => {
        const { newValue: companyCode, data } = params;

        const batchUpdateData = companyCode
          ? {
            ...lookUpData.obligationBasedInitialData[companyCode]
              ? mapValues(
                obligationBasedFieldsToCopy,
                value => lookUpData.obligationBasedInitialData[companyCode][value]
              )
              : {},
            ...mapValues(
              companyCodeBasedFieldsToCopy,
              value => lookUpData.companyCodeBasedInitialData[companyCode][value]
            ),
            ...copyWhenDomicileCountryCode({ data: { ...data, companyCode }, lookUpData }),
          }
          : {};

        setRowData({
          ...params,
          batchUpdateData,
          batchUpdateChangeTouched: false,
        });
      },
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.companyCodeOptions,
    },
  },
  {
    baseColumnDefinition: {
      ...legalEntityColumnDefinition,
      headerName: 'Legal Entity',
    },
  },
  {
    baseColumnDefinition: countryCodeColumnDefinition,
  },
  {
    baseColumnDefinition: countryTaxTypeColumnDefinition,
    editableWhen: [GridMode.Add],
    editableWhenConfig: {
      valueSetterFactory: ({ lookUpData, setRowData }) => params => {
        const { newValue: countryTaxType, data } = params;

        const countryTaxTypeRelatedData = getDataFromCountryTaxType(
          countryTaxType,
          lookUpData.taxCurrencyDefaultCodes,
          lookUpData.countryTaxTypesOptions
        );

        setRowData({
          ...params,
          batchUpdateData: {
            ...countryTaxTypeRelatedData,
            ...copyWhenDomicileCountryCode({
              data: { ...data, countryCode: countryTaxTypeRelatedData.countryCode },
              lookUpData,
            }),
          },
          batchUpdateChangeTouched: false,
        });
      },
      valueGetter: ({ data }) => encodeCountryTaxType(data.countryCode, data.countryTaxType),
      cellRenderer: ({ value }) => decodeCountryTaxType(value).countryTaxType,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.countryTaxTypesOptions,
    },
  },
  {
    baseColumnDefinition: countryTaxTypeDescriptionColumnDefinition,
  },
  {
    baseColumnDefinition: chartNameColumnDefinition,
    withSourceIndicator: true,
    editableWhen: [GridMode.Edit, GridMode.Add],
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.chartNameOptions,
    },
  },
  {
    baseColumnDefinition: {
      headerName: 'Connection',
      field: 'connectionName',
      ...alignLeftCellStyle,
      minWidth: 100,
    },
    withSourceIndicator: true,
    editableWhen: [GridMode.Edit, GridMode.Add],
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.connectionOptions,
      cellEditorParams: {
        withClearIcon: true,
      },
    },
  },
  {
    baseColumnDefinition: usTaxTypeColumnDefinition,
    withSourceIndicator: true,
    editableWhen: [GridMode.Edit, GridMode.Add],
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.usTaxTypeOptions,
    },
  },
  {
    baseColumnDefinition: localTaxTypeColumnDefinition,
    withSourceIndicator: true,
    editableWhen: [GridMode.Edit, GridMode.Add],
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.localTaxTypeOptions,
      cellEditorParams: {
        withClearIcon: true,
      },
    },
  },
  {
    baseColumnDefinition: functionalCurrencyColumnDefinition,
    withSourceIndicator: true,
    editableWhen: [GridMode.Edit, GridMode.Add],
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.currencyCodeOptions,
    },
  },
  {
    baseColumnDefinition: statCurrencyColumnDefinition,
    withSourceIndicator: true,
    editableWhen: [GridMode.Edit, GridMode.Add],
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.currencyCodeOptions,
    },
  },
  {
    baseColumnDefinition: {
      ...taxCurrencyColumnDefinition,
      field: 'taxCurrency',
    },
    withSourceIndicator: true,
    editableWhen: [GridMode.Edit, GridMode.Add],
    editableWhenConfig: {
      additionalCondition: isTaxCurrencyFieldEditable,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.currencyCodeOptions,
    },
  },
  {
    baseColumnDefinition: secondaryTaxColumnDefinition,
    withSourceIndicator: true,
    editableWhen: [GridMode.Edit],
    editableWhenConfig: {
      additionalCondition: hasDomicileCountryCode,
      valueSetterFactory: ({ setRowData }) => params => {
        const { newValue: secondaryTax } = params;
        setRowData({
          ...params,
          batchUpdateData: {
            secondaryTax,
            homeOfficeCompanyCode: secondaryTax === SecondaryTax.Branch ? params.data.homeOfficeCompanyCode : null,
          },
          batchUpdateChangeTouched: false,
        });
      },
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.SecondaryTaxOptions,
    },
  },
  {
    baseColumnDefinition: homeOfficeCompanyCodeColumnDefinition,
    editableWhen: [GridMode.Edit],
    editableWhenConfig: {
      additionalCondition: isSecondaryTaxBranch,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: (context, rowParams) => {
        const newOptions = [];
        const ccFiltered = context.homeOfficeCompanyCodeOptions.filter(item =>
          rowParams.data.connectionName === item.connName
          && item.enabledFlag === 'Y'
          && rowParams.data.enabledFlag === 'Y'
          && context.companyCodeBasedInitialData[rowParams.data.companyCode].subledgerFlag === item.subledgerFlag
        );

        const obligationArray = Object.values(context.obligationBasedInitialData);
        const obligationsFiltered = obligationArray.filter(item =>
          item.connectionName === rowParams.data.connectionName
          && item.enabledFlag === 'Y'
          && item.countryCode === 'US'
          && item.countryTaxType === 'US2'
        );

        if (obligationsFiltered.length > 0) {
          ccFiltered.forEach(option => {
            obligationsFiltered.forEach(obligation => {
              if (option?.value === obligation?.companyCode) {
                if (!newOptions.some(existingOption => existingOption.value === option.value)) {
                  newOptions.push(option);
                }
              }
            });
          });
        }
        return newOptions;
      },
      cellEditorParams: {
        withClearIcon: true,
      },
    },
  },
  {
    baseColumnDefinition: recordFTCColumnDefinition,
    withSourceIndicator: true,
    editableWhen: [GridMode.Edit],
    editableWhenConfig: {
      additionalCondition: hasDomicileCountryCode,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.RecordFTCOptions,
      cellEditorParams: {
        withClearIcon: true,
      },
    },
  },
  {
    baseColumnDefinition: federalFTCLimitColumnDefinition,
    editableWhen: [GridMode.Edit, GridMode.Add],
    editableWhenConfig: {
      additionalCondition: areUSFederalFieldsEditable,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.FederalFTCLimitOptions,
    },
  },
  {
    baseColumnDefinition: stateMinimumTaxColumDefinition,
    editableWhen: [GridMode.Edit, GridMode.Add],
    editableWhenConfig: {
      valueParser: nullableFloatParser,
      additionalCondition: areStateFieldsEditable,
    },
  },
  {
    baseColumnDefinition: stateApportionmentRateColumnDefinition,
    editableWhen: [GridMode.Edit, GridMode.Add],
    editableWhenConfig: {
      valueParser: nullableFloatParser,
      additionalCondition: areStateFieldsEditable,
    },
  },
  {
    baseColumnDefinition: statePrePostNOLColumnDefinition,
    editableWhen: [GridMode.Edit, GridMode.Add],
    editableWhenConfig: {
      additionalCondition: isStatePrePostNOLFieldEditable,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.StatePrePostNOLOptions,
    },
  },
  {
    baseColumnDefinition: filingGroupColumnDefinition,
    editableWhen: [GridMode.Edit, GridMode.Add],
    withDropdown: true,
    editableWhenConfig: {
      additionalCondition: areStateFieldsEditable,
    },
    withDropdownConfig: {
      optionsGetter: context => context.filingGroupOptions,
      cellEditorParams: {
        withClearIcon: true,
      },
    },
  },
  {
    baseColumnDefinition: NOLColumnDefinition,
    editableWhen: [GridMode.Edit, GridMode.Add],
    editableWhenConfig: {
      additionalCondition: isNOLFieldEditable,
      valueSetterFactory: ({ setRowData }) => params => {
        const { newValue: NOL, data } = params;
        setRowData({
          ...params,
          batchUpdateData: {
            statePrePostNOL: NOL === NOLValues.UTILIZE ? data.statePrePostNOL : null,
            recordVA: NOL === NOLValues.RECORD ? data.recordVA : null,
          },
        });
      },
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.NOLOptions,
    },
  },
  {
    baseColumnDefinition: recordVAColumnDefinition,
    editableWhen: [GridMode.Edit, GridMode.Add],
    editableWhenConfig: {
      additionalCondition: isRecordVAEditable,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.RecordVAOptions,
    },
  },
  {
    baseColumnDefinition: currentTaxRateColumnDefinition,
    withPopup: true,
    withPopupConfig: {
      cellEditor: NumericCellEditor,
    },
    editableWhen: [GridMode.Edit, GridMode.Add],
    editableWhenConfig: {
      valueParser: floatParser,
    },
    withSourceIndicator: true,
    withSourceIndicatorConfig: {
      formatter: percentageFormatter,
    },
  },
  {
    baseColumnDefinition: {
      ...deferredTaxRateColumnDefinition,
      field: 'deferredTaxRate',
    },
    withPopup: true,
    withPopupConfig: {
      cellEditor: NumericCellEditor,
    },
    editableWhen: [GridMode.Edit, GridMode.Add],
    editableWhenConfig: {
      valueParser: floatParser,
    },
    withSourceIndicator: true,
    withSourceIndicatorConfig: {
      formatter: percentageFormatter,
    },
  },
  {
    baseColumnDefinition: commentColumnDefinition,
    withPopup: true,
    withPopupConfig: {
      cellEditor: 'agLargeTextCellEditor',
    },
    editableWhen: [GridMode.Edit, GridMode.Add],
  },
]);


const getBaseColumnDefinitions = ({ setRowData, lookUpData }) => appendIndicatorRenderer(
  columnDefinitionConfig.map(
    ({
      baseColumnDefinition,
      editableWhen,
      editableWhenConfig,
      withSourceIndicator,
      withSourceIndicatorConfig,
      withDropdown,
      withDropdownConfig,
      withPopup,
      withPopupConfig,
    }) => ({
      ...addDefaultProperties(baseColumnDefinition),
      ...editableWhen
        ? addEditableProperties(baseColumnDefinition, editableWhen, setRowData, editableWhenConfig, lookUpData)
        : {},
      ...withSourceIndicator
        ? addSourceIndicatorProperties(baseColumnDefinition, withSourceIndicatorConfig)
        : {},
      ...withDropdown
        ? addWithDropdownProperties(baseColumnDefinition, withDropdownConfig, lookUpData)
        : {},
      ...withPopup
        ? addPopupProperties(withPopupConfig?.cellEditor)
        : {},
    }))
);


const getAdminColumnDefinition = ({ editRow, context }) => addDefaultProperties({
  ...adminColumnSharedFields,
  cellRenderer: ObligationsAdminColumnRenderer,
  cellRendererParams: params => {
    const cellParams = {
      editRow,
      context,
    };

    const rowIndex = params.rowIndex ?? params.node?.rowIndex;
    if (typeof rowIndex === 'number') {
      cellParams.rowIndex = rowIndex;
    }

    return cellParams;
  },
  valueGetter: () => '',
  getQuickFilterText: getQuickFilterTextWithSkippingAddedRow,
  minWidth: 80,
});


export default ({ editRow, setRowData, lookUpData, context }) => ([
  getAdminColumnDefinition({
    editRow,
    context,
  }),
  {
    ...diagnosticsSeverityColumnDefinition,
    cellClass: 'disable-editing',
  },
  {
    ...diagnosticWithTooltipColumnDefinition,
    cellClass: 'disable-editing',
  },
  ...getBaseColumnDefinitions({
    setRowData,
    lookUpData,
  }),
]);

